import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  type AdminUser,
  type SystemRole,
  fetchAdminUsers,
  fetchCompanies,
  fetchRoles,
} from "../../services/adminApi";
import { fullName } from "./users/shared";
import { EditUserModal } from "./users/EditUserModal";
import { AddUserModal } from "./users/AddUserModal";

const PAGE_SIZE = 50;

/**
 * Users tab (users.manage). List users (platform: all; company admin: own
 * company), edit each user's role + box visibility (derived vs explicit list),
 * and add a user.
 *
 * The list is server-paginated + server-searched (the whole directory used to
 * come down in one response, each row carrying a role OUTER APPLY). The search
 * box is debounced and resets to the first page; Prev/Next walk the pages.
 *
 * The row-detail editor + add form live in ./users/* (EditUserModal,
 * AddUserModal and their shared pieces) — extracted from this file to keep it
 * legible.
 */
export const UsersTab: React.FC = () => {
  const { token, role, can } = useAuth();
  // The per-user "View permissions" controls (pricing + dashboard tiles) are a
  // platform-operator affordance. The tab itself is already users.manage-gated;
  // also require platform.
  const canManageViewPerms = role === "platform" && can("users.manage");
  const canManageCompanies = can("companies.manage");

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [companies, setCompanies] = useState<{ id: number; name: string; type: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editUid, setEditUid] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  // Search box value + its debounced echo. Only the debounced value hits the
  // server, and a query change snaps back to the first page.
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(id);
  }, [query]);

  const refresh = useCallback(() => {
    if (!token) return;
    fetchAdminUsers(token, { q: debouncedQuery || undefined, limit: PAGE_SIZE, offset })
      .then((res) => {
        setUsers(res.users);
        setTotal(res.total);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token, debouncedQuery, offset]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Roles + (for companies.manage admins) the full company directory drive the
  // Add-user modal. Company admins can't read that directory, so they fall back
  // to the companies present on their own scoped page (which is just theirs).
  useEffect(() => {
    if (!token) return;
    fetchRoles(token).then(setRoles).catch(() => setRoles([]));
    if (canManageCompanies) {
      fetchCompanies(token)
        .then((cs) =>
          setCompanies(
            cs.map((c) => ({
              id: c.company_id,
              name: c.company_name ?? `#${c.company_id}`,
              type: c.company_type,
            })),
          ),
        )
        .catch(() => setCompanies([]));
    }
  }, [token, canManageCompanies]);

  // Company id -> type, preferring the directory fetch and supplementing with
  // whatever's on the loaded user page.
  const companyTypeById = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of users ?? []) {
      if (u.company_id != null && u.company_type) m.set(u.company_id, u.company_type);
    }
    for (const c of companies) m.set(c.id, c.type);
    return m;
  }, [users, companies]);

  // Company options for "Add user": the directory for platform admins,
  // supplemented by companies present on the loaded page (a company admin only
  // ever sees their own).
  const companyOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const u of users ?? []) {
      if (u.company_id != null && !seen.has(u.company_id)) {
        seen.set(u.company_id, u.company_name ?? `#${u.company_id}`);
      }
    }
    for (const c of companies) seen.set(c.id, c.name);
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [users, companies]);

  const rangeLabel =
    total === 0
      ? "No users"
      : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, company…"
          className="border border-secondary-300 rounded px-3 py-1.5 text-sm w-full md:max-w-sm"
        />
        <Button size="sm" onClick={() => setAdding(true)} disabled={companyOptions.length === 0}>
          + Add user
        </Button>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {!users ? (
        <LoadingSpinner label="loading users" />
      ) : (
        <>
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2">Visibility</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-secondary-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{fullName(u)}</div>
                      {u.email && <div className="text-xs text-secondary-500">{u.email}</div>}
                    </td>
                    <td className="px-3 py-2 text-secondary-700">{u.company_name ?? "—"}</td>
                    <td className="px-3 py-2">{u.role_name ?? <em className="text-secondary-400">none</em>}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          "text-[11px] px-1.5 py-0.5 rounded " +
                          (u.scu_access_mode === "explicit"
                            ? "bg-warning-100 text-warning-700"
                            : "bg-secondary-100 text-secondary-600")
                        }
                      >
                        {u.scu_access_mode === "explicit" ? "explicit list" : "company default"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => setEditUid(u.user_id)}
                      >
                        edit
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-secondary-500">
                      No users.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-secondary-500">{rangeLabel}</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {editUid !== null && (
        <EditUserModal
          token={token ?? ""}
          uid={editUid}
          roles={roles}
          canManageViewPerms={canManageViewPerms}
          onClose={() => setEditUid(null)}
          onDone={() => {
            setEditUid(null);
            refresh();
          }}
        />
      )}
      {adding && (
        <AddUserModal
          token={token ?? ""}
          roles={roles}
          companies={companyOptions}
          usersByCompanyType={(cid) => companyTypeById.get(cid) ?? null}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default UsersTab;
